import React, { useEffect, useState, useCallback } from "react";
import { K } from "../theme";
import { _register, _unregister } from "../notify";

// Custom alert/confirm modal host. Renders ONCE at App level; receives
// imperative push events from src/notify.js and shows them as a stack
// (multiple notifies queued behind the current one). For confirm, we
// resolve the caller's Promise on OK / Cancel / overlay-click.
//
// Why not use window.alert / window.confirm directly? Browser-native
// dialogs show the parent context title at the top — "Claude" inside
// the Claude preview, an empty box in headless tests, the URL in some
// older browsers — and they're styled per-OS, which clashes with the
// rest of the app's chrome. This component takes full control.

const variantColors = {
  info:    { bg: K.bl, border: '#bfdbfe' },
  success: { bg: K.g,  border: '#bbf7d0' },
  error:   { bg: K.r,  border: '#fecaca' },
  warn:    { bg: K.a,  border: '#fde68a' },
  danger:  { bg: K.r,  border: '#fecaca' },
};

export const NotifyHost = () => {
  const [queue, setQueue] = useState([]);

  const push = useCallback((item) => {
    setQueue(q => [...q, { ...item, _id: Math.random().toString(36).slice(2) }]);
  }, []);

  useEffect(() => {
    _register(push);
    return () => _unregister();
  }, [push]);

  // What's typed into a prompt dialog, reset as each one opens.
  const [text, setText] = useState('');
  // Only flag "can't be blank" once they've tried to submit — showing it the
  // moment an empty prompt opens reads as an error they haven't made yet.
  const [tried, setTried] = useState(false);
  useEffect(() => {
    setText(queue[0]?.kind === 'prompt' ? (queue[0].defaultValue || '') : '');
    setTried(false);
  }, [queue]);

  // Pop without accepting: confirm resolves to the boolean given, a
  // dismissed prompt resolves to null (same contract as window.prompt).
  const pop = useCallback((resolveWith) => {
    setQueue(q => {
      const [head, ...rest] = q;
      if (typeof head?.resolve === 'function') {
        if (head.kind === 'prompt') head.resolve(null);
        else head.resolve(resolveWith === undefined ? false : resolveWith);
      }
      return rest;
    });
  }, []);

  // Submitting a prompt: stash the text on the item so pop() can resolve
  // with it, then close.
  const submitPrompt = useCallback(() => {
    const head = queue[0];
    if (head?.kind !== 'prompt') return;
    if (head.required && !String(text).trim()) { setTried(true); return; }  // keep it open
    if (typeof head.resolve === 'function') head.resolve(text);
    setQueue(q => q.slice(1));
  }, [queue, text]);

  // Keyboard: Esc closes (cancel for confirm), Enter accepts.
  useEffect(() => {
    if (!queue.length) return;
    const onKey = (e) => {
      const isPrompt = queue[0]?.kind === 'prompt';
      if (e.key === 'Escape') { pop(false); }
      // A multiline prompt keeps Enter for newlines; Ctrl/Cmd+Enter submits.
      else if (e.key === 'Enter') {
        if (!isPrompt) pop(true);
        else if (!queue[0].multiline || e.ctrlKey || e.metaKey) { e.preventDefault(); submitPrompt(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [queue, pop, submitPrompt]);

  if (!queue.length) return null;
  const item = queue[0];
  const variant = variantColors[item.variant] || variantColors.info;
  const blocked = item.kind === 'prompt' && item.required && !String(text).trim();

  return (
    <div
      onClick={() => pop(false)}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 12, width: 'min(440px, 100%)',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)', overflow: 'hidden',
          border: `1px solid ${K.b1 || '#e5e7eb'}`,
        }}
      >
        {/* Slim accent bar — color carries the semantic meaning */}
        <div style={{ height: 4, background: variant.bg }} />
        <div style={{ padding: '18px 20px 16px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: K.t0 || '#111', marginBottom: 8 }}>
            {item.title}
          </div>
          <div style={{ fontSize: 12, color: K.t1 || '#374151', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
            {item.message}
          </div>
          {item.kind === 'prompt' && (
            item.multiline ? (
              <textarea
                autoFocus
                value={text}
                placeholder={item.placeholder}
                onChange={e => setText(e.target.value)}
                style={{
                  width: '100%', marginTop: 10, minHeight: 78, resize: 'vertical',
                  padding: '9px 11px', fontSize: 13, color: K.t0 || '#111',
                  background: K.bg2 || '#f0f2f5', border: `1px solid ${K.b0 || '#e5e7eb'}`,
                  borderRadius: 10, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
                }}
              />
            ) : (
              <input
                autoFocus
                type={item.inputType}
                value={text}
                placeholder={item.placeholder}
                onChange={e => setText(e.target.value)}
                style={{
                  width: '100%', marginTop: 10,
                  padding: '9px 11px', fontSize: 13, color: K.t0 || '#111',
                  background: K.bg2 || '#f0f2f5', border: `1px solid ${K.b0 || '#e5e7eb'}`,
                  borderRadius: 10, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
                }}
              />
            )
          )}
          {item.kind === 'prompt' && item.required && tried && !String(text).trim() && (
            <div style={{ fontSize: 11, color: K.t3 || '#64748b', marginTop: 6 }}>This can't be left blank.</div>
          )}
        </div>
        <div style={{
          padding: '10px 20px 16px',
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          {(item.kind === 'confirm' || item.kind === 'prompt') && (
            <button
              onClick={() => pop(false)}
              style={{
                padding: '7px 14px', fontSize: 11, fontWeight: 600,
                color: K.t1 || '#374151', background: 'transparent',
                border: `1px solid ${K.b1 || '#e5e7eb'}`, borderRadius: 8, cursor: 'pointer',
              }}
            >{item.cancelLabel || 'Cancel'}</button>
          )}
          <button
            autoFocus={item.kind !== 'prompt'}
            onClick={() => (item.kind === 'prompt' ? submitPrompt() : pop(true))}
            disabled={blocked}
            style={{
              padding: '7px 14px', fontSize: 11, fontWeight: 700,
              color: 'white', background: variant.bg,
              border: 'none', borderRadius: 8,
              cursor: blocked ? 'not-allowed' : 'pointer',
              opacity: blocked ? 0.5 : 1,
            }}
          >{item.kind === 'alert' ? 'OK' : (item.okLabel || 'OK')}</button>
        </div>
      </div>
    </div>
  );
};
